import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '../hooks/useAuth'

interface ProtectedRouteProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

export default function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return fallback || (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Checking authentication...
        </Typography>
      </Box>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return (
      <Navigate 
        to="/login" 
        state={{ from: location.pathname }} 
        replace 
      />
    )
  }

  // Render protected content
  return <>{children}</>
}

// Higher-order component version for class components or other use cases
export function withAuthGuard<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  return function AuthGuardedComponent(props: P) {
    return (
      <ProtectedRoute>
        <Component {...props} />
      </ProtectedRoute>
    )
  }
}

// Hook for checking specific permissions or roles
export function useAuthGuard() {
  const { isAuthenticated, user, isLoading } = useAuth()

  const hasRole = (role: string): boolean => {
    if (!user || !isAuthenticated) return false
    
    // Check user attributes for roles
    // This would depend on how roles are stored in Cognito user attributes
    const userRoles = user.signInDetails?.loginId || ''
    return typeof userRoles === 'string' ? userRoles.includes(role) : false
  }

  const hasPermission = (): boolean => {
    if (!user || !isAuthenticated) return false
    
    // Check user attributes for permissions
    // This would depend on how permissions are stored in Cognito user attributes
    return true // Placeholder - implement based on your permission system
  }

  const requireAuth = (): boolean => {
    return isAuthenticated && !!user
  }

  return {
    isAuthenticated,
    isLoading,
    user,
    hasRole,
    hasPermission,
    requireAuth
  }
}